import { type FeatureData, type SrpProductData } from "~/utils/models";
import classes from "~/features/selectionAssistant/gearbox/components/ResultsSection/components/ResultTile/ResultTile.module.less";
import classNames from "classnames";
import { Card } from "~/features/configurator/Linear/components/Card";
import { PrimaryImage } from "~/domains/product/components/PrimaryImage";
import { ProductContext } from "~/domains/product/ProductContext";
import { ShortDescription } from "~/domains/product/components/ShortDescription";
import { Designation } from "~/domains/product/components/Designation";
import { PreferredIndicator } from "~/domains/product/components/PreferredIndicator";
import { SideButton } from "~/common/components/Button/SideButton";
import { IconType } from "~/common/components/icon/IconType";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Button } from "~/common/mui";
import { Icon } from "~/common/components/icon/Icon";
import { PRIMARY_BTN_VARIANT, SECONDARY_BTN_VARIANT } from "~/common/components/Button/constants";
import { ProductComparisonCheckbox } from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { createColumnHelper } from "@tanstack/react-table";
import { PlainFormattedNumber } from "~/common/components/PlainFormattedNumber";
import { Trans, useTranslation } from "react-i18next";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { LinkType } from "~/common/components/Link";

interface Props {
  className?: string;
  product: SrpProductData;
}

export const ResultTile = ({ className, product }: Props) => {
  const data = product.features.map((feature) => ({
    label: getHeaderCell(feature),
    val: getDataCell(feature),
  }));

  const ch = createColumnHelper<(typeof data)[number]>();

  return (
    <ProductContext value={product}>
      <Card
        className={classNames(classes.tile, className)}
        disableHover={true}
        tracking-aux-item-name-marker={product.designation}
        tracking-aux-item-id-marker={product.sapMaterialNumber}
      >
        <div className={classes.divInfo}>
          <PrimaryImage className={classes.image} />
          <div className={classes.description}>
            <div className={classes.title}>
              <PreferredIndicator />
              <Designation className={classes.designation} linkProps={{ variant: LinkType.NAVIGATION }} />
            </div>
            <ShortDescription linkProps={{ variant: LinkType.NAVIGATION }} />
          </div>
          <div className={classes.table}>
            <TanStackTable
              variant={"stripes"}
              data={data}
              columns={[
                ch.accessor("label", { header: "", cell: (cell) => cell.renderValue(), size: 0.5 }),
                ch.accessor("val", { header: "", cell: (cell) => cell.renderValue(), size: 0.5 }),
              ]}
              fullwidth={true}
            />
          </div>
        </div>
        <div className={classes.divActions}>
          <div>
            <ProductComparisonCheckbox gaLocation={"Gearbox SA"} productCode={product.code} />
          </div>
          <Actions product={product} />
        </div>
      </Card>
    </ProductContext>
  );
};

const Actions = ({ product }: { product: SrpProductData }) => {
  const isMinTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const [addToWishlistCb] = useAddProductToWishlistFlow({
    productInput: product,
  });

  const showCartButton = canSeeAddToCartButton();
  const showWishlistButton = canSeeWishlistButton();

  let wishlistButton = null;
  if (showWishlistButton) {
    if (showCartButton && !isMinTablet) {
      wishlistButton = (
        <SideButton
          icon={IconType.Bookmark}
          onClick={() => addToWishlistCb()}
          tracking-aux-item-wishlist-button-marker="Wishlist"
        />
      );
    } else {
      const variant = showCartButton ? SECONDARY_BTN_VARIANT : PRIMARY_BTN_VARIANT;
      wishlistButton = (
        <Button
          startIcon={<Icon type={IconType.Bookmark} />}
          {...variant}
          size={"medium"}
          fullWidth={true}
          onClick={() => addToWishlistCb()}
          tracking-aux-item-wishlist-button-marker="Wishlist"
        >
          {t("page.sa.gearbox.btn.addToWishlist")}
        </Button>
      );
    }
  }

  const addToCartCb = () => {
    AddToCartService.openAddToCartWizard(
      {
        restrictionQuery: "",
        productCode: product.code,
      },
      {
        page: "GSA",
      },
    );
  };

  return (
    <div className={classes.actions}>
      {showCartButton && (
        <Button
          startIcon={<Icon type={IconType.Cart} />}
          size={"medium"}
          fullWidth={true}
          onClick={addToCartCb}
          tracking-aux-item-details-button-marker="Add to Cart"
        >
          {t("page.sa.gearbox.btn.addToCart")}
        </Button>
      )}
      {wishlistButton}
    </div>
  );
};

const getHeaderCell = (feature: FeatureData) => {
  return <Trans i18nKey={`facet.${feature.techFilterId}.symbol`} />;
};

const getDataCell = (feature: FeatureData) => {
  return (
    <>
      {feature.values.map((value, index) => (
        <div key={index}>
          {+value ? <PlainFormattedNumber value={+value || 0} /> : value}{" "}
          <Trans i18nKey={`facet.${feature.techFilterId}.unit`} />
        </div>
      ))}
    </>
  );
};
